/**
 * ScheduleDetailPage — Full detail/config page for a single LPA schedule.
 *
 * Features:
 * - Breadcrumb: Audits > {Audit Name} > {Schedule Name}
 * - Schedule metadata card (name, frequency, auditor, dates, active status)
 * - Edit schedule via dialog
 * - Delete schedule with confirmation
 * - Full question assignment section (reuses ScheduleQuestionAssignment)
 * - Active toggle inline
 *
 * Routed at /lpa/audits/:auditId/schedules/:scheduleId
 */

import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/core/LanguageContext';
import { useTenant } from '@/core/hooks/useTenant';
import { useAuditById } from '../hooks/useAudits';
import {
  useScheduleById,
  useUpdateSchedule,
  useDeleteSchedule,
} from '../hooks/useSchedules';
import {
  useInstancesByScheduleId,
  useGenerateInstance,
} from '../hooks/useScheduleInstances';
import type {
  LpaScheduleWithDetails,
  LpaScheduleFrequency,
  LpaScheduleInstanceWithDetails,
  LpaInstanceStatus,
} from '../types';
import ScheduleFormDialog from './ScheduleFormDialog';
import ScheduleQuestionAssignment from './ScheduleQuestionAssignment';
import InstanceStatusDialog from './InstanceStatusDialog';
import InstanceAuditorDialog from './InstanceAuditorDialog';
import InstanceDeleteDialog from './InstanceDeleteDialog';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { Separator } from '@/shared/ui/separator';
import { Switch } from '@/shared/ui/switch';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/ui/card';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/ui/breadcrumb';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  UserCircle,
  Repeat,
  AlertTriangle,
  Plus,
  HelpCircle,
  Snowflake,
  Clock,
  MoreHorizontal,
  SkipForward,
  UserRoundPen,
  Ban,
  ListChecks,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Frequency config (shared constant — same as ScheduleListSection)
// ---------------------------------------------------------------------------

const FREQUENCY_CONFIG: Record<
  LpaScheduleFrequency,
  { labelKey: string; fallback: string; className: string }
> = {
  per_shift: {
    labelKey: 'mod_lpa.schedules.freq.perShift',
    fallback: 'Per Shift',
    className: 'bg-violet-500/15 text-violet-700 border-violet-300 dark:text-violet-400 dark:border-violet-600',
  },
  daily: {
    labelKey: 'mod_lpa.schedules.freq.daily',
    fallback: 'Daily',
    className: 'bg-blue-500/15 text-blue-700 border-blue-300 dark:text-blue-400 dark:border-blue-600',
  },
  weekly: {
    labelKey: 'mod_lpa.schedules.freq.weekly',
    fallback: 'Weekly',
    className: 'bg-emerald-500/15 text-emerald-700 border-emerald-300 dark:text-emerald-400 dark:border-emerald-600',
  },
  monthly: {
    labelKey: 'mod_lpa.schedules.freq.monthly',
    fallback: 'Monthly',
    className: 'bg-amber-500/15 text-amber-700 border-amber-300 dark:text-amber-400 dark:border-amber-600',
  },
  quarterly: {
    labelKey: 'mod_lpa.schedules.freq.quarterly',
    fallback: 'Quarterly',
    className: 'bg-rose-500/15 text-rose-700 border-rose-300 dark:text-rose-400 dark:border-rose-600',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Period computation — for generating the next instance
// ---------------------------------------------------------------------------

// TODO(lpa-timezone): Date-only conversions currently use `toISOString()` and
// `new Date('YYYY-MM-DD')` in this file. This can shift displayed/stored days
// in non-UTC timezones. Keep as-is for demo/manual generation, then replace
// with timezone-safe local date helpers during production hardening.
function toISODate(d: Date): string {
  return d.toISOString().split('T')[0];
}

/**
 * Get the period boundaries that contain a given date, for a given frequency.
 */
function getPeriodContaining(
  frequency: LpaScheduleFrequency,
  date: Date,
): { periodStart: Date; periodEnd: Date } {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  switch (frequency) {
    case 'per_shift':
    case 'daily': {
      return { periodStart: new Date(d), periodEnd: new Date(d) };
    }
    case 'weekly': {
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      const weekStart = new Date(d);
      weekStart.setDate(weekStart.getDate() + diff);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return { periodStart: weekStart, periodEnd: weekEnd };
    }
    case 'monthly': {
      const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
      const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return { periodStart: monthStart, periodEnd: monthEnd };
    }
    case 'quarterly': {
      const qMonth = Math.floor(d.getMonth() / 3) * 3;
      const qStart = new Date(d.getFullYear(), qMonth, 1);
      const qEnd = new Date(d.getFullYear(), qMonth + 3, 0);
      return { periodStart: qStart, periodEnd: qEnd };
    }
  }
}

/**
 * Advance a date by one period step based on frequency.
 */
function advancePeriod(date: Date, frequency: LpaScheduleFrequency): Date {
  const next = new Date(date);
  switch (frequency) {
    case 'per_shift':
    case 'daily':
      next.setDate(next.getDate() + 1);
      break;
    case 'weekly':
      next.setDate(next.getDate() + 7);
      break;
    case 'monthly':
      next.setMonth(next.getMonth() + 1);
      break;
    case 'quarterly':
      next.setMonth(next.getMonth() + 3);
      break;
  }
  return next;
}

/**
 * Compute the next period to generate for a schedule based on existing instances.
 * Returns null if the schedule can't generate (no auditor, past end_date, etc.)
 */
function computeNextPeriod(
  schedule: LpaScheduleWithDetails,
  existingInstances: LpaScheduleInstanceWithDetails[],
): { periodStart: string; periodEnd: string; dueDate: string } | null {
  if (!schedule.auditor_id) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const generatedPeriodStarts = new Set(existingInstances.map((i) => i.period_start));

  // Find the last generated instance by period_start
  const sorted = [...existingInstances].sort((a, b) =>
    a.period_start.localeCompare(b.period_start),
  );

  let cursor: Date;
  if (sorted.length > 0) {
    const lastPeriodStart = new Date(sorted[sorted.length - 1].period_start);
    cursor = advancePeriod(lastPeriodStart, schedule.frequency);
  } else {
    // Start from schedule start_date or today, whichever is later
    const schedStart = new Date(schedule.start_date);
    cursor = schedStart > today ? schedStart : today;
  }

  const scheduleEnd = schedule.end_date ? new Date(schedule.end_date) : null;

  // Walk forward until we find the first not-yet-generated period.
  // This keeps demo generation idempotent even if old/manual rows exist.
  const MAX_GENERATION_STEPS = 400;
  for (let step = 0; step < MAX_GENERATION_STEPS; step += 1) {
    const period = getPeriodContaining(schedule.frequency, cursor);

    if (scheduleEnd && period.periodStart > scheduleEnd) {
      return null;
    }

    const periodStart = toISODate(period.periodStart);
    if (!generatedPeriodStarts.has(periodStart)) {
      const dueDate = new Date(period.periodEnd);
      dueDate.setHours(23, 59, 59, 999);

      return {
        periodStart,
        periodEnd: toISODate(period.periodEnd),
        dueDate: dueDate.toISOString(),
      };
    }

    cursor = advancePeriod(period.periodStart, schedule.frequency);
  }

  return null;
}

function formatPeriod(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);

  if (start === end) {
    return s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}–${e.getDate()}`;
  }

  return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

function formatDueDate(dueDateStr: string): string {
  const due = new Date(dueDateStr);
  return due.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Instance status config
// ---------------------------------------------------------------------------

const INSTANCE_STATUS_CONFIG: Record<
  LpaInstanceStatus,
  { labelKey: string; fallback: string; className: string; dotColor: string }
> = {
  pending: {
    labelKey: 'mod_lpa.instances.status.pending',
    fallback: 'Pending',
    className: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
    dotColor: 'bg-slate-400',
  },
  in_progress: {
    labelKey: 'mod_lpa.instances.status.inProgress',
    fallback: 'In Progress',
    className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
    dotColor: 'bg-blue-500',
  },
  completed: {
    labelKey: 'mod_lpa.instances.status.completed',
    fallback: 'Completed',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
    dotColor: 'bg-emerald-500',
  },
  overdue: {
    labelKey: 'mod_lpa.instances.status.overdue',
    fallback: 'Overdue',
    className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
    dotColor: 'bg-red-500',
  },
  skipped: {
    labelKey: 'mod_lpa.instances.status.skipped',
    fallback: 'Skipped',
    className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
    dotColor: 'bg-amber-500',
  },
  cancelled: {
    labelKey: 'mod_lpa.instances.status.cancelled',
    fallback: 'Cancelled',
    className: 'bg-gray-50 text-gray-500 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-700',
    dotColor: 'bg-gray-400',
  },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
        <div className="text-sm mt-0.5">{children}</div>
      </div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-5 w-64" />
      <Skeleton className="h-[160px] w-full rounded-lg" />
      <Skeleton className="h-[300px] w-full rounded-lg" />
    </div>
  );
}

function NotFoundState({ t, auditId }: { t: (key: string, fallback: string) => string; auditId?: string }) {
  const navigate = useNavigate();
  const backUrl = auditId ? `/lpa/audits/${auditId}` : '/lpa?tab=audits';

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
      <h3 className="text-lg font-medium mb-1">
        {t('mod_lpa.schedules.detail.notFound', 'Schedule not found')}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-4">
        {t(
          'mod_lpa.schedules.detail.notFoundHint',
          'This schedule may have been deleted or you may not have access.',
        )}
      </p>
      <Button variant="outline" onClick={() => navigate(backUrl)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('mod_lpa.schedules.detail.backToAudit', 'Back to Audit')}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedule Instances sub-component
// ---------------------------------------------------------------------------

function InstanceStatusBadge({
  status,
  t,
}: {
  status: LpaInstanceStatus;
  t: (key: string, fallback: string) => string;
}) {
  const config = INSTANCE_STATUS_CONFIG[status];
  return (
    <Badge variant="outline" className={`text-xs ${config.className}`}>
      <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${config.dotColor}`} />
      {t(config.labelKey, config.fallback)}
    </Badge>
  );
}

function ScheduleInstancesSection({
  schedule,
  auditId,
  isArchived,
}: {
  schedule: LpaScheduleWithDetails;
  auditId: string;
  isArchived: boolean;
}) {
  const { t } = useLanguage();
  const { selectedTenantId } = useTenant();
  const { data: instances, isLoading } = useInstancesByScheduleId(schedule.id);
  const generateInstanceMut = useGenerateInstance();

  // Action dialog state
  const [statusDialogInstance, setStatusDialogInstance] =
    useState<LpaScheduleInstanceWithDetails | null>(null);
  const [auditorDialogInstance, setAuditorDialogInstance] =
    useState<LpaScheduleInstanceWithDetails | null>(null);
  const [deleteDialogInstance, setDeleteDialogInstance] =
    useState<LpaScheduleInstanceWithDetails | null>(null);

  // Compute the next period to generate
  const nextPeriod = useMemo(() => {
    if (!instances) return null;
    return computeNextPeriod(schedule, instances);
  }, [schedule, instances]);

  const handleGenerate = () => {
    if (!nextPeriod || !selectedTenantId || !schedule.auditor_id) return;
    generateInstanceMut.mutate({
      schedule_id: schedule.id,
      audit_id: auditId,
      auditor_id: schedule.auditor_id,
      period_start: nextPeriod.periodStart,
      period_end: nextPeriod.periodEnd,
      due_date: nextPeriod.dueDate,
      auditId,
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">
              {t('mod_lpa.instances.scheduleInstancesTitle', 'Generated Instances')}
            </CardTitle>
            {(instances?.length ?? 0) > 0 && (
              <Badge variant="secondary" className="text-xs">
                {instances?.length ?? 0}
              </Badge>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="right" className="w-80 text-sm">
                <div className="space-y-2">
                  <p className="font-medium">
                    {t('mod_lpa.instances.generateHelp.title', 'Manual Instance Generation')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'mod_lpa.instances.generateHelp.body',
                      'Each click generates one instance for the next period. Instances are immediately frozen — schedule changes won\'t affect them. In production, instances are generated automatically.',
                    )}
                  </p>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Generate button */}
          {!isArchived && schedule.is_active && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    onClick={handleGenerate}
                    disabled={
                      !nextPeriod ||
                      !schedule.auditor_id ||
                      generateInstanceMut.isPending
                    }
                  >
                    {generateInstanceMut.isPending ? (
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-1.5" />
                    )}
                    {t('mod_lpa.instances.generateNext', 'Generate Next')}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[280px] text-xs">
                  {!schedule.auditor_id
                    ? t('mod_lpa.instances.generateNoAuditor', 'Assign an auditor to generate instances.')
                    : !nextPeriod
                      ? t('mod_lpa.instances.generateNoPeriod', 'No more periods to generate (schedule may have ended).')
                      : t(
                          'mod_lpa.instances.generateNextHint',
                          'Generate a frozen instance for {period}',
                        ).replace(
                          '{period}',
                          formatPeriod(nextPeriod.periodStart, nextPeriod.periodEnd),
                        )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {nextPeriod && schedule.auditor_id && !isArchived && schedule.is_active && (
          <CardDescription>
            {t('mod_lpa.instances.nextPeriodLabel', 'Next period: {period}').replace(
              '{period}',
              formatPeriod(nextPeriod.periodStart, nextPeriod.periodEnd),
            )}
          </CardDescription>
        )}
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-[44px] w-full rounded-lg" />
            ))}
          </div>
        ) : !instances || instances.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center rounded-lg border border-dashed">
            <ListChecks className="h-7 w-7 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              {t(
                'mod_lpa.instances.scheduleEmpty',
                'No instances generated yet. Use the "Generate Next" button to create the first instance for this schedule.',
              )}
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {instances.map((instance) => {
              const canModify =
                !isArchived &&
                (instance.status === 'pending' ||
                  instance.status === 'in_progress' ||
                  instance.status === 'overdue' ||
                  instance.status === 'skipped' ||
                  instance.status === 'cancelled');
              const canDelete = !isArchived && instance.status === 'pending';

              return (
                <div
                  key={instance.id}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                >
                  {/* Frozen indicator */}
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Snowflake className="h-4 w-4 text-sky-500 shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent className="text-xs">
                        {t(
                          'mod_lpa.instances.frozenTooltip',
                          "Generated & frozen — schedule changes won't affect this instance",
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  {/* Auditor */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="text-[10px]">
                        {getInitials(instance.auditor.full_name, instance.auditor.email)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-muted-foreground max-w-[120px] truncate">
                      {instance.auditor.full_name ?? instance.auditor.email}
                    </span>
                  </div>

                  {/* Period */}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                    <CalendarDays className="h-3 w-3" />
                    <span>
                      {formatPeriod(instance.period_start, instance.period_end)}
                    </span>
                  </div>

                  {/* Due date */}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                    <Clock className="h-3 w-3" />
                    <span>{formatDueDate(instance.due_date)}</span>
                  </div>

                  {/* Status */}
                  <div className="shrink-0">
                    <InstanceStatusBadge status={instance.status} t={t} />
                  </div>

                  {/* Spacer */}
                  <div className="flex-1" />

                  {/* Actions dropdown */}
                  {!isArchived && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {canModify && (
                          <>
                            <DropdownMenuItem onClick={() => setStatusDialogInstance(instance)}>
                              <SkipForward className="h-4 w-4 mr-2" />
                              {t('mod_lpa.instances.actions.changeStatus', 'Change Status')}
                            </DropdownMenuItem>
                            {(instance.status === 'pending' || instance.status === 'in_progress') && (
                              <DropdownMenuItem onClick={() => setAuditorDialogInstance(instance)}>
                                <UserRoundPen className="h-4 w-4 mr-2" />
                                {t('mod_lpa.instances.actions.reassignAuditor', 'Reassign Auditor')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                          </>
                        )}
                        {canDelete && (
                          <DropdownMenuItem
                            onClick={() => setDeleteDialogInstance(instance)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t('mod_lpa.instances.actions.delete', 'Delete')}
                          </DropdownMenuItem>
                        )}
                        {!canModify && !canDelete && (
                          <DropdownMenuItem disabled>
                            <Ban className="h-4 w-4 mr-2" />
                            {t('mod_lpa.instances.actions.noActions', 'No actions available')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Action dialogs */}
      <InstanceStatusDialog
        instance={statusDialogInstance}
        onClose={() => setStatusDialogInstance(null)}
        auditId={auditId}
      />
      <InstanceAuditorDialog
        instance={auditorDialogInstance}
        onClose={() => setAuditorDialogInstance(null)}
        auditId={auditId}
      />
      <InstanceDeleteDialog
        instance={deleteDialogInstance}
        onClose={() => setDeleteDialogInstance(null)}
        auditId={auditId}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ScheduleDetailPage() {
  const { auditId, scheduleId } = useParams<{ auditId: string; scheduleId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Data
  const { data: audit, isLoading: auditLoading } = useAuditById(auditId);
  const { data: schedule, isLoading: scheduleLoading } = useScheduleById(scheduleId);
  const updateSchedule = useUpdateSchedule();
  const deleteScheduleHook = useDeleteSchedule();

  // UI state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isLoading = auditLoading || scheduleLoading;
  const isArchived = audit?.status === 'archived';

  // Handlers
  const handleEdit = (data: {
    name: string;
    frequency: LpaScheduleFrequency;
    auditor_id: string | null;
    is_active: boolean;
    start_date: string;
    end_date: string | null;
  }) => {
    if (!scheduleId || !auditId) return;
    updateSchedule.mutate(
      {
        scheduleId,
        auditId,
        input: {
          name: data.name,
          frequency: data.frequency,
          auditor_id: data.auditor_id,
          is_active: data.is_active,
          start_date: data.start_date,
          end_date: data.end_date,
        },
      },
      { onSuccess: () => setEditDialogOpen(false) },
    );
  };

  const handleActiveToggle = (checked: boolean) => {
    if (!scheduleId || !auditId) return;
    updateSchedule.mutate({
      scheduleId,
      auditId,
      input: { is_active: checked },
    });
  };

  const handleDelete = () => {
    if (!scheduleId || !auditId) return;
    deleteScheduleHook.mutate(
      { scheduleId, auditId },
      { onSuccess: () => navigate(`/lpa/audits/${auditId}`) },
    );
  };

  // Loading
  if (isLoading) {
    return (
      <div className="space-y-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <Skeleton className="h-4 w-16" />
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <DetailSkeleton />
      </div>
    );
  }

  // Not found
  if (!schedule) {
    return <NotFoundState t={t} auditId={auditId} />;
  }

  const freqConfig = FREQUENCY_CONFIG[schedule.frequency];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              href="/lpa?tab=audits"
              onClick={(e) => {
                e.preventDefault();
                navigate('/lpa?tab=audits');
              }}
            >
              {t('mod_lpa.audits.detail.breadcrumbAudits', 'Audits')}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink
              href={`/lpa/audits/${auditId}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/lpa/audits/${auditId}`);
              }}
            >
              {audit?.name ?? '...'}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{schedule.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <CardTitle className="text-xl">{schedule.name}</CardTitle>
              <Badge variant="outline" className={`text-xs shrink-0 ${freqConfig.className}`}>
                {t(freqConfig.labelKey, freqConfig.fallback)}
              </Badge>
              {!schedule.is_active && (
                <Badge variant="secondary" className="text-xs shrink-0">
                  {t('mod_lpa.schedules.inactive', 'Inactive')}
                </Badge>
              )}
            </div>

            {/* Actions */}
            {!isArchived && (
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-2 mr-2">
                  <span className="text-xs text-muted-foreground">
                    {t('mod_lpa.schedules.form.activeLabel', 'Active')}
                  </span>
                  <Switch
                    checked={schedule.is_active}
                    onCheckedChange={handleActiveToggle}
                    disabled={updateSchedule.isPending}
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditDialogOpen(true)}
                >
                  <Pencil className="h-4 w-4 mr-1.5" />
                  {t('mod_lpa.audits.detail.edit', 'Edit')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <Separator />

        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Frequency */}
            <DetailRow
              icon={Repeat}
              label={t('mod_lpa.schedules.form.frequencyLabel', 'Frequency')}
            >
              <Badge variant="outline" className={`text-xs ${freqConfig.className}`}>
                {t(freqConfig.labelKey, freqConfig.fallback)}
              </Badge>
            </DetailRow>

            {/* Auditor */}
            <DetailRow
              icon={UserCircle}
              label={t('mod_lpa.schedules.form.auditorLabel', 'Auditor')}
            >
              {schedule.auditor ? (
                <div className="flex items-center gap-2">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="text-[10px]">
                      {getInitials(schedule.auditor.full_name, schedule.auditor.email)}
                    </AvatarFallback>
                  </Avatar>
                  <span>{schedule.auditor.full_name ?? schedule.auditor.email}</span>
                </div>
              ) : (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {t('mod_lpa.schedules.noAuditor', 'No auditor')}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[240px] text-xs">
                      {t(
                        'mod_lpa.schedules.noAuditorHint',
                        'Instances cannot be generated without an auditor assigned.',
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </DetailRow>

            {/* Start date */}
            <DetailRow
              icon={CalendarDays}
              label={t('mod_lpa.schedules.form.startDateLabel', 'Start Date')}
            >
              {formatDate(schedule.start_date)}
            </DetailRow>

            {/* End date */}
            <DetailRow
              icon={CalendarDays}
              label={t('mod_lpa.schedules.form.endDateLabel', 'End Date')}
            >
              {schedule.end_date
                ? formatDate(schedule.end_date)
                : t('mod_lpa.schedules.detail.indefinite', 'Indefinite')}
            </DetailRow>
          </div>
        </CardContent>
      </Card>

      {/* Question assignment section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t('mod_lpa.schedules.questions.title', 'Assigned Questions')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScheduleQuestionAssignment
            scheduleId={schedule.id}
            auditId={schedule.audit_id}
            isArchived={!!isArchived}
          />
        </CardContent>
      </Card>

      {/* Instance generation & management — Phase 3 */}
      <ScheduleInstancesSection
        schedule={schedule}
        auditId={auditId!}
        isArchived={!!isArchived}
      />

      {/* Edit dialog */}
      <ScheduleFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        schedule={schedule}
        onSubmit={handleEdit}
        isSubmitting={updateSchedule.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('mod_lpa.schedules.deleteTitle', 'Delete schedule?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'mod_lpa.schedules.deleteDescription',
                `This will permanently delete "${schedule.name}" and all its question assignments. This action cannot be undone.`,
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('mod_lpa.schedules.deleteCancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteScheduleHook.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('mod_lpa.schedules.deleteConfirm', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
